import type { ProvenanceEntry, ProvenanceIndex, ProvenanceState } from '../bpmn/provenance';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';

export interface ProvenancePanelProps {
  index: ProvenanceIndex;
  /** 選択中の要素。未選択なら全件のカタログだけを出す。 */
  elementId: string | null;
  onSelectElement(elementId: string): void;
}

/**
 * BPMNの出典表示。
 *
 * relationship と textAnnotation には図上の配置（DI）が無いため、線が描けなくても
 * サイドパネルだけで全件を読めることを必須にしている。
 * 出典本文はテキストとして描画し、HTMLとして解釈しない（Reactの既定の挙動に任せる）。
 */
export function ProvenancePanel({ index, elementId, onSelectElement }: ProvenancePanelProps) {
  const entries = elementId ? index.entriesFor(elementId) : [];
  const unknowns = index.unknowns();
  const catalog = index.catalog();

  return (
    <div className="provenance-panel">
      {elementId && (
        <section className="provenance-section">
          <h3>選択中の要素の出典</h3>
          <p className="hint">{index.labelFor(elementId)}</p>
          {entries.length === 0 ? (
            <p className="hint">この要素に紐づく出典はありません（出典未記載）。</p>
          ) : (
            <ul className="provenance-entries">
              {entries.map((entry) => (
                // 1つのrelationshipが複数のtargetを持てるので、relationshipIdだけでは一意にならない。
                <li key={`${entry.relationshipId}:${entry.annotationId}`}>
                  <ProvenanceStateBadge state={entry.state} />
                  <p className="provenance-text">{describeEntry(entry)}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="provenance-section">
        <h3>
          出典不明 <Badge tone="unknown">{unknowns.length}</Badge>
        </h3>
        {unknowns.length === 0 ? (
          <p className="hint">出典不明の要素はありません。</p>
        ) : (
          <ul className="provenance-entries">
            {unknowns.map(({ elementId: target, entry }) => (
              <li key={`${target}:${entry.relationshipId}:${entry.annotationId}`}>
                <Button variant="text" onClick={() => onSelectElement(target)}>
                  {index.labelFor(target)}
                </Button>
                <p className="provenance-text">{describeEntry(entry)}</p>
              </li>
            ))}
          </ul>
        )}
        <p className="hint">出典不明だけを理由に業務の実行は止まりません。</p>
      </section>

      <section className="provenance-section">
        <h3>
          出典カタログ <Badge tone="neutral">{catalog.length}</Badge>
        </h3>
        {catalog.length === 0 ? (
          <p className="hint">textAnnotation / relationship による出典がこの定義にはありません。</p>
        ) : (
          <ul className="provenance-catalog">
            {catalog.map((source) => (
              <li key={source.id}>
                <p className="provenance-text">{source.text || '（本文が空です）'}</p>
                <div className="provenance-usedby">
                  {source.usedBy.length === 0 ? (
                    <span className="hint">未参照</span>
                  ) : (
                    source.usedBy.map((target) => (
                      <Button key={target} variant="text" onClick={() => onSelectElement(target)}>
                        {index.labelFor(target)}
                      </Button>
                    ))
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const STATE_LABELS: Record<ProvenanceState, { tone: 'neutral' | 'unknown' | 'failed'; label: string }> =
  {
    documented: { tone: 'neutral', label: '根拠' },
    unknownSource: { tone: 'unknown', label: '出典不明' },
    missingText: { tone: 'unknown', label: '出典未記載' },
    danglingRef: { tone: 'failed', label: '参照先不明' },
  };

function ProvenanceStateBadge({ state }: { state: ProvenanceState }) {
  const view = STATE_LABELS[state];
  return <Badge tone={view.tone}>{view.label}</Badge>;
}

function describeEntry(entry: ProvenanceEntry): string {
  if (entry.state === 'danglingRef') {
    return `参照先の textAnnotation "${entry.annotationId}" が定義内に見つかりません。`;
  }
  if (entry.text === null || entry.text === '') {
    return `textAnnotation "${entry.annotationId}" に本文がありません。`;
  }
  return entry.text;
}
